import './VocabularyPractice.css';
import apiExtension, { BASE_URL } from './api_extension';
import Login from './Login';
import { useState, useEffect, useCallback, useRef } from 'react';

// 富文本解析函数，增加HTML内容过滤以增强安全性
const parse = (html) => {
  if (!html || typeof html !== 'string') return <></>;
  
  try {
    // 过滤危险标签和属性
    let filteredHtml = html
      // 移除script标签
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      // 移除iframe标签
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
      // 移除on*事件属性
      .replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '')
      // 移除javascript:链接
      .replace(/href\s*=\s*["']javascript:[^"']*["']/gi, '');
    
    return (
      <div dangerouslySetInnerHTML={{ __html: filteredHtml }} />
    );
  } catch (error) {
    console.error('富文本解析失败:', error);
    return <div style={{ color: '#999', fontStyle: 'italic' }}>内容解析失败</div>;
  }
};

const VocabularyExtension = () => {
  // 状态管理
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [vocabList, setVocabList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isLogin, setIsLogin] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const audioRef = useRef(null);
  const [filterKeyword, setFilterKeyword] = useState('');
  const [displayStates, setDisplayStates] = useState({}); // 为每个词汇单独管理显示状态

  // 自定义日志函数
  const logDebug = useCallback((message, data = null) => {
    const timestamp = new Date().toLocaleTimeString();
    console.log(`[${timestamp}] ${message}`, data);
  }, []);

  // 检查登录状态
  const checkLoginStatus = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const userInfo = await apiExtension.getUserInfo();
        setCurrentUser(userInfo);
        setIsLogin(true);
        logDebug('用户已登录', userInfo);
      } catch (err) {
        logDebug('获取用户信息失败', err.message);
        apiExtension.logout();
        setIsLogin(false);
        setCurrentUser(null);
      }
    } else {
      setIsLogin(false);
      setCurrentUser(null);
    }
  }, [logDebug]);

  // 初始检查登录状态
  useEffect(() => {
    checkLoginStatus();
  }, [checkLoginStatus]);

  // 获取分类数据
  const fetchCategories = useCallback(async () => {
    if (!isLogin) return;

    setLoading(true);
    setError('');
    try {
      logDebug('开始请求分类数据');
      const categories = await apiExtension.getVocabularyCategories();
      setCategories(categories);
      logDebug('分类加载完成', { 数量: categories.length });
    } catch (err) {
      logDebug('分类加载失败', err.message);
      setError(`分类加载失败: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [isLogin, logDebug]);

  // 加载分类词汇（扩展版本）
  const loadVocabByCategory = useCallback(async () => {
    if (!selectedCategory || !isLogin) return;

    setLoading(true);
    setError('');
    try {
      logDebug('开始加载分类扩展词汇', { categoryId: selectedCategory });
      const params = new URLSearchParams({
        categories__id: selectedCategory,
        page_size: 10000,
        include_extensions: true, // 请求包含扩展信息
        include_sentences: true   // 请求包含例句
      });
      const vocab = await apiExtension.getVocabulary(params.toString());
      setVocabList(vocab);
      logDebug('分类扩展词汇加载完成', { 数量: vocab.length });
      // 添加详细日志，显示返回的数据结构
      if (vocab.length > 0) {
        logDebug('第一个词汇数据结构示例:', {
          hasExtensionInfo: !!vocab[0].extension_info,
          hasSynonyms: !!vocab[0].synonyms,
          hasAntonyms: !!vocab[0].antonyms,
          hasExampleSentences: !!vocab[0].example_sentences,
          exampleSentencesType: vocab[0].example_sentences ? typeof vocab[0].example_sentences : 'undefined',
          exampleSentencesLength: vocab[0].example_sentences && Array.isArray(vocab[0].example_sentences) ? vocab[0].example_sentences.length : 'n/a'
        });
      }
    } catch (err) {
      logDebug('分类扩展词汇加载失败', err.message);
      setError(`词汇加载失败: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, isLogin, logDebug]);

  // 登录成功后的处理
  const handleLogin = (userData) => {
    setCurrentUser(userData);
    setIsLogin(true);
    logDebug('登录成功', userData);
    fetchCategories();
  };

  // 登出处理
  const handleLogout = () => {
    apiExtension.logout();
    setIsLogin(false);
    setCurrentUser(null);
    setVocabList([]);
    setCategories([]);
    logDebug('用户已登出');
  };

  // 统一的音频播放函数，增加空指针防护
  const playAudio = useCallback((vocab) => {
    if (!vocab || !vocab.pronunciation_url) return;

    // 停止之前可能在播放的音频，增加空指针防护
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    } catch (error) {
      logDebug('停止音频播放时出错:', error.message);
    }

    audioRef.current = new Audio(vocab.pronunciation_url);
    audioRef.current.play()
      .then(() => {
        setAudioPlaying(true);
        logDebug('音频播放开始', { url: vocab.pronunciation_url });
      })
      .catch(err => {
        logDebug('音频播放失败', err.message);
        setError(`音频播放失败: ${err.message}`);
        setAudioPlaying(false);
      });

    audioRef.current.onended = () => {
      setAudioPlaying(false);
      logDebug('音频播放结束');
    };
  }, [logDebug]);

  // 渲染媒体文件（图片或视频），使用React状态控制错误显示
  const renderMedia = (mediaUrl, altText) => {
    // 如果mediaUrl为空，直接返回
    if (!mediaUrl) {
      logDebug('媒体URL为空');
      return null;
    }

    logDebug('原始媒体URL:', mediaUrl);

    // 根据当前环境调整媒体文件URL
    let adjustedUrl = mediaUrl;
    
    // 检查是否已经是完整URL
    if (mediaUrl.startsWith('http://') || mediaUrl.startsWith('https://')) {
      // 已经是完整URL，直接使用
      logDebug('使用完整URL:', adjustedUrl);
    } else {
      // 如果是相对路径，确保以/开头
      if (!mediaUrl.startsWith('/')) {
        adjustedUrl = '/' + mediaUrl;
        logDebug('添加前缀后URL:', adjustedUrl);
      }
      
      // 确保URL以/media/开头
      if (!adjustedUrl.startsWith('/media/')) {
        if (adjustedUrl.includes('/media/')) {
          // 提取media/及其后面的部分
          const mediaIndex = adjustedUrl.indexOf('/media/');
          adjustedUrl = adjustedUrl.substring(mediaIndex);
        } else if (adjustedUrl.includes('media/')) {
          // 如果URL中包含media/但前面没有/
          const mediaIndex = adjustedUrl.indexOf('media/');
          adjustedUrl = '/' + adjustedUrl.substring(mediaIndex);
        } else {
          // 否则，添加/media/前缀
          adjustedUrl = '/media/' + (adjustedUrl.startsWith('/') ? adjustedUrl.substring(1) : adjustedUrl);
        }
        logDebug('适应环境后的URL:', adjustedUrl);
      }
      
      // 为阿里云环境构建完整URL
      const baseUrl = window.location.origin;
      adjustedUrl = baseUrl + adjustedUrl;
      logDebug('在阿里云环境中使用完整URL:', adjustedUrl);
    }

    // 根据文件扩展名判断是图片还是视频
    const fileExtension = adjustedUrl.split('.').pop()?.toLowerCase() || '';
    const videoExtensions = ['mp4', 'webm', 'ogg'];
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'];

    if (videoExtensions.includes(fileExtension)) {
      // 视频文件 - 使用React方式处理错误
      return (
        <div style={{ position: 'relative' }}>
          <video
            src={adjustedUrl}
            alt={altText || '词汇视频'}
            controls
            style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
            onError={(e) => {
              logDebug('视频加载失败', { requestPath: adjustedUrl, originalUrl: mediaUrl });
              // 使用React状态方式处理错误
              e.target.style.display = 'none';
              const errorMsg = e.target.parentNode.querySelector('.video-error-message');
              if (errorMsg) {
                errorMsg.style.display = 'block';
              }
            }}
          />
          <div className="video-error-message" style={{ display: 'none', color: '#999', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
            视频加载失败
          </div>
        </div>
      );
    } else if (imageExtensions.includes(fileExtension)) {
      // 图片文件
      return (
        <img
          src={adjustedUrl}
          alt={altText || '词汇图片'}
          style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
          onError={(e) => {
            console.error('媒体加载失败，请求路径:', adjustedUrl);
            console.error('媒体文件名:', mediaUrl);
            // 设置默认图片
            e.target.onerror = null;
            e.target.src = `${BASE_URL}/placeholder-image.png`;
          }}
        />
      );
    }

    // 不支持的媒体类型
    return <div style={{ color: '#999', fontStyle: 'italic' }}>不支持的文件类型</div>;
  };

  // 过滤词汇列表
  const filteredVocabs = vocabList.filter(vocab => 
    vocab.chinese.toLowerCase().includes(filterKeyword.toLowerCase()) ||
    vocab.english.toLowerCase().includes(filterKeyword.toLowerCase())
  );

  // 确保词汇项有显示状态，默认都为隐藏
  const ensureDisplayState = (vocabId) => {
    if (!displayStates[vocabId]) {
      setDisplayStates(prev => ({
        ...prev,
        [vocabId]: {
          showDetails: false,
          showExtensions: false,
          showSentences: false
        }
      }));
      return false; // 返回初始化状态
    }
    return true; // 状态已存在
  };

  // 切换详情显示
  const toggleDetails = (vocabId) => {
    // 确保显示状态存在
    ensureDisplayState(vocabId);
    
    // 切换显示状态
    setDisplayStates(prev => ({
      ...prev,
      [vocabId]: {
        ...prev[vocabId],
        showDetails: !prev[vocabId].showDetails
      }
    }));
  };

  // 切换词汇扩展显示
  const toggleVocabularyExtensions = (vocabId) => {
    // 确保显示状态存在
    ensureDisplayState(vocabId);
    
    // 切换显示状态
    setDisplayStates(prev => ({
      ...prev,
      [vocabId]: {
        ...prev[vocabId],
        showExtensions: !prev[vocabId].showExtensions
      }
    }));
  };

  // 切换例句显示
  const toggleExampleSentences = (vocabId) => {
    // 确保显示状态存在
    ensureDisplayState(vocabId);
    
    // 切换显示状态
    setDisplayStates(prev => ({
      ...prev,
      [vocabId]: {
        ...prev[vocabId],
        showSentences: !prev[vocabId].showSentences
      }
    }));
  };

  // 处理例句渲染的工具函数（提取为独立函数，避免IIFE解析歧义）
  const renderExampleSentences = useCallback((exampleSentences) => {
    // 首先检查是否为数组
    if (!Array.isArray(exampleSentences)) {
      logDebug('例句数据不是数组类型', { type: typeof exampleSentences, value: exampleSentences });
      return <div style={{ color: '#999', fontStyle: 'italic' }}>暂无例句或数据格式异常</div>;
    }

    // 先过滤得到有效的例句数据
    const filteredSentences = exampleSentences.filter(sentence => {
      // 必须是对象且不为null
      if (typeof sentence !== 'object' || sentence === null) {
        logDebug('过滤无效例句：不是有效对象', { sentence });
        return false;
      }
      
      // 检查是否包含必要的中英文字段
      const hasEnglish = !!(sentence.english || sentence.English || sentence.english_text || sentence.English_text || sentence.EngLish || sentence.english_Text);
      const hasChinese = !!(sentence.chinese || sentence.Chinese || sentence.chinese_text || sentence.Chinese_text || sentence.Chinese_Text || sentence.chinese_Text);
      
      if (!hasEnglish || !hasChinese) {
        logDebug('过滤无效例句：缺少必要的中英文字段', { sentence });
        return false;
      }
      
      return true;
    });

    // 然后对过滤后的句子进行映射和渲染
    const validSentences = filteredSentences
      .map((sentence, index) => {
        try {
          // 尝试处理并渲染有效数据
          if (typeof sentence !== 'object' || sentence === null) {
            throw new Error(`无效的例句格式: ${JSON.stringify(sentence)}`);
          }

          // 增强的结构检查，添加更多可能的字段名
          const english = sentence.english || sentence.English || sentence.english_text || sentence.English_text || sentence.EngLish || sentence.english_Text || '';
          const chinese = sentence.chinese || sentence.Chinese || sentence.chinese_text || sentence.Chinese_text || sentence.Chinese_Text || sentence.chinese_Text || '';

          // 添加更详细的调试信息
          logDebug(`[VocabularyExtension] 例句${index + 1}:`, {
            english,
            chinese,
            hasPronunciation: !!sentence.pronunciation_url,
            allAvailableFields: Object.keys(sentence),
            sentenceObject: sentence
          });

          return (
            <li key={index} style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: index < filteredSentences.length - 1 ? '1px solid #eee' : 'none' }}>
              <div style={{ marginBottom: '5px', fontSize: '14px', color: '#333' }}>{english || '暂无英文例句'}</div>
              <div style={{ fontSize: '13px', color: '#666' }}>{chinese || '暂无中文翻译'}</div>
              {sentence.pronunciation_url && (
                <button
                  onClick={() => { playAudio({ pronunciation_url: sentence.pronunciation_url }); }}
                  disabled={audioPlaying}
                  style={{
                    marginTop: '8px',
                    padding: '3px 8px',
                    backgroundColor: audioPlaying ? '#cccccc' : '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: audioPlaying ? 'not-allowed' : 'pointer',
                    fontSize: '11px'
                  }}
                >
                  {audioPlaying ? '播放中...' : '发音'}
                </button>
              )}
            </li>
          );
        } catch (err) {
          // 打印错误但不中断渲染
          console.warn(`跳过无效例句 [索引: ${index}]`, err.message);
          return null; // 跳过无效条目
        }
      })
      .filter(Boolean); // 移除null值

    // 根据过滤后的有效例句数量决定显示内容
    return validSentences.length > 0 ? (
      <ul style={{ listStyleType: 'none', padding: 0 }}>
        {validSentences}
      </ul>
    ) : (
      <div style={{ color: '#999', fontStyle: 'italic' }}>暂无有效例句</div>
    );
  }, [logDebug, playAudio, audioPlaying]);

  // 组件挂载时加载分类
  useEffect(() => {
    if (isLogin) {
      fetchCategories();
    }
  }, [isLogin, fetchCategories]);

  // 分类变化时加载词汇
  useEffect(() => {
    if (selectedCategory) {
      loadVocabByCategory();
    }
  }, [selectedCategory, loadVocabByCategory]);

  // 组件卸载时停止音频，增加空指针防护
  useEffect(() => {
    return () => {
      try {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
      } catch (error) {
        console.warn('组件卸载时停止音频出错:', error.message);
      }
    };
  }, []);

  // 未登录状态
  if (!isLogin) {
    return (
      <div className="login-container">
        <h2>请先登录</h2>
        <Login onLogin={handleLogin} />
      </div>
    );
  }

  // 加载状态
  if (loading) return <div className="loading">加载中...</div>;

  // 错误状态
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="container">
      <div className="user-info">
        <span>欢迎，{currentUser.username}</span>
        <button onClick={handleLogout} className="logout-button">登出</button>
      </div>
      <h2>词汇扩展练习</h2>

      {/* 分类选择 */}
      <div className="category-selector" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>
        <label style={{ marginRight: '10px', fontWeight: 'bold' }}>选择分类：</label>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          disabled={loading}
          style={{ padding: '8px 15px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '200px' }}
        >
          <option value="">请选择分类</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
        <span className="count-info" style={{ marginLeft: '10px', color: '#666' }}>
          共 {vocabList.length} 条
        </span>
      </div>

      {/* 词汇搜索 */}
      {vocabList.length > 0 && (
        <div className="search-container" style={{ marginBottom: '20px' }}>
          <input
            type="text"
            placeholder="搜索词汇..."
            value={filterKeyword}
            onChange={(e) => setFilterKeyword(e.target.value)}
            style={{ padding: '8px 15px', border: '1px solid #ddd', borderRadius: '4px', width: '300px' }}
          />
          {filterKeyword && (
            <button 
              onClick={() => setFilterKeyword('')}
              style={{ marginLeft: '10px', padding: '8px 15px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              清除搜索
            </button>
          )}
        </div>
      )}

      {/* 词汇列表展示（按类型名称分类） */}
      {selectedCategory && (
        <div className="vocab-list-section" style={{ marginBottom: '20px' }}>
          <h3>扩展词汇列表</h3>
          {filteredVocabs.length > 0 ? (
            <div className="vocab-list">
              {filteredVocabs.map(vocab => (
                <div key={vocab.id} className="vocab-item" style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                  {/* 词汇头部 */}
                  <div className="vocab-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div>
                      <span style={{ fontSize: '18px', fontWeight: 'bold', marginRight: '15px' }}>{vocab.chinese}</span>
                      <span style={{ fontSize: '16px', color: '#666' }}>{vocab.english}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => playAudio(vocab)}
                        disabled={!vocab.pronunciation_url || audioPlaying}
                        style={{
                          padding: '5px 12px',
                          backgroundColor: !vocab.pronunciation_url ? '#e0e0e0' : (audioPlaying ? '#cccccc' : '#2196F3'),
                          color: !vocab.pronunciation_url ? '#999' : 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: !vocab.pronunciation_url ? 'not-allowed' : 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        {vocab.pronunciation_url ? (audioPlaying ? '播放中...' : '发音') : '无发音'}
                      </button>
                      <button
                        onClick={() => toggleDetails(vocab.id)}
                        style={{
                          padding: '5px 12px',
                          backgroundColor: displayStates[vocab.id]?.showDetails ? '#FF9800' : '#e0e0e0',
                          color: displayStates[vocab.id]?.showDetails ? 'white' : '#666',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        {displayStates[vocab.id]?.showDetails ? '隐藏' : '查看'}详情
                      </button>
                      <button
                        onClick={() => toggleVocabularyExtensions(vocab.id)}
                        style={{
                          padding: '5px 12px',
                          backgroundColor: displayStates[vocab.id]?.showExtensions ? '#4CAF50' : '#e0e0e0',
                          color: displayStates[vocab.id]?.showExtensions ? 'white' : '#666',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        {displayStates[vocab.id]?.showExtensions ? '隐藏' : '查看'}扩展
                      </button>
                      <button
                        onClick={() => toggleExampleSentences(vocab.id)}
                        style={{
                          padding: '5px 12px',
                          backgroundColor: displayStates[vocab.id]?.showSentences ? '#2196F3' : '#e0e0e0',
                          color: displayStates[vocab.id]?.showSentences ? 'white' : '#666',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        {displayStates[vocab.id]?.showSentences ? '隐藏' : '查看'}例句
                      </button>
                    </div>
                  </div>
                  
                  {/* 词汇详情 - 独立显示 */}
                  {displayStates[vocab.id]?.showDetails && (
                    <div className="vocab-details" style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e9ecef' }}>
                      {/* 媒体展示 */}
                      {vocab.image_url && (
                        <div className="media-container" style={{ marginBottom: '15px', textAlign: 'center' }}>
                          {renderMedia(vocab.image_url, vocab.chinese)}
                        </div>
                      )}
                        
                      {/* 解释说明 */}
                      {vocab.remark && (
                        <div className="remark-section" style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #ddd' }}>
                          <h4 style={{ margin: '0 0 10px 0' }}>解释说明</h4>
                          {parse(vocab.remark)}
                        </div>
                      )}
                    </div>
                  )}
                    
                  {/* 扩展信息 - 独立显示区块 */}
                  {displayStates[vocab.id]?.showExtensions && (
                    <div className="extension-section" style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e9ecef' }}>
                      <div style={{ padding: '10px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #ddd' }}>
                        <h4 style={{ margin: '0 0 10px 0' }}>扩展信息</h4>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                          数据结构检查: {JSON.stringify({ 
                            hasExtensionInfo: !!vocab.extension_info, 
                            hasSynonyms: !!vocab.synonyms, 
                            hasAntonyms: !!vocab.antonyms,
                            hasVocabularyExtensions: Array.isArray(vocab.vocabulary_extensions) && vocab.vocabulary_extensions.length > 0
                          })}
                        </div>
                        
                        {/* 检查是否有vocabulary_extensions数组 */}
                        {Array.isArray(vocab.vocabulary_extensions) && vocab.vocabulary_extensions.length > 0 ? (
                          // 按类型归类显示扩展信息
                          <div>
                            {/* 按relation_type或relation_type_display分组 */}
                            {(() => {
                              // 先创建分组对象
                              const groupedExtensions = {};
                              
                              // 遍历扩展项进行分组
                              vocab.vocabulary_extensions.forEach(ext => {
                                const type = ext.relation_type_display || ext.relation_type || '其他';
                                if (!groupedExtensions[type]) {
                                  groupedExtensions[type] = [];
                                }
                                groupedExtensions[type].push(ext);
                              });
                              
                              // 返回分组后的JSX结构
                              return Object.entries(groupedExtensions).map(([type, exts]) => (
                                <div key={type} style={{ marginBottom: '20px' }}>
                                  {/* 显示类型标题 */}
                                  <h5 style={{ 
                                    margin: '0 0 10px 0', 
                                    padding: '5px 10px', 
                                    backgroundColor: '#e3f2fd', 
                                    borderRadius: '4px', 
                                    color: '#1976d2',
                                    display: 'inline-block' 
                                  }}>
                                    {type} ({exts.length})
                                  </h5>
                                  
                                  {/* 显示该类型下的所有扩展项 */}
                                  <div style={{ marginLeft: '10px' }}>
                                    {exts.map((ext, index) => (
                                      <div key={index} style={{ 
                                        marginBottom: '10px', 
                                        padding: '10px', 
                                        backgroundColor: '#f9f9f9', 
                                        borderRadius: '4px',
                                        borderLeft: '3px solid #2196F3' 
                                      }}>
                                        <div style={{ marginBottom: '5px' }}>
                                          <span style={{ marginLeft: '5px' }}><strong>{ext.english}</strong> - {ext.chinese}</span>
                                        </div>
                                        {ext.remark && (
                                          <div style={{ fontSize: '13px', color: '#666' }}>{parse(ext.remark)}</div>
                                        )}
                                        {ext.pronunciation_url && (
                                          <button
                                            onClick={() => { playAudio({ pronunciation_url: ext.pronunciation_url }); }}
                                            disabled={audioPlaying}
                                            style={{
                                              marginTop: '8px',
                                              padding: '3px 8px',
                                              backgroundColor: audioPlaying ? '#cccccc' : '#2196F3',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '3px',
                                              cursor: audioPlaying ? 'not-allowed' : 'pointer',
                                              fontSize: '11px'
                                            }}
                                          >
                                            {audioPlaying ? '播放中...' : '发音'}
                                          </button>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ));
                            })()}
                          </div>
                        ) : (
                          // 保持原有的扩展信息显示逻辑作为备选
                          <>
                            {vocab.extension_info ? (
                              <div style={{ marginBottom: '10px' }}>{parse(vocab.extension_info)}</div>
                            ) : (
                              <div style={{ color: '#999', fontStyle: 'italic' }}>暂无扩展说明</div>
                            )}
                            {vocab.synonyms && (
                              <div style={{ marginBottom: '10px' }}>
                                <strong>同义词:</strong> {vocab.synonyms}
                              </div>
                            )}
                            {vocab.antonyms && (
                              <div>
                                <strong>反义词:</strong> {vocab.antonyms}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                    
                  {/* 例句部分（修复核心：用独立函数替代IIFE，避免解析歧义） */}
                  {displayStates[vocab.id]?.showSentences && (
                    <div className="sentences-section" style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e9ecef' }}>
                      <div style={{ padding: '10px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #ddd' }}>
                        <h4 style={{ margin: '0 0 10px 0' }}>例句</h4>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                          例句数据检查: {JSON.stringify({ 
                            hasExampleSentences: !!vocab.example_sentences, 
                            exampleSentencesType: vocab.example_sentences ? typeof vocab.example_sentences : 'undefined', 
                            exampleSentencesIsArray: Array.isArray(vocab.example_sentences), 
                            exampleSentencesLength: Array.isArray(vocab.example_sentences) ? vocab.example_sentences.length : 'not array',
                            sampleData: Array.isArray(vocab.example_sentences) && vocab.example_sentences.length > 0 ? 
                              { 
                                firstSentenceType: typeof vocab.example_sentences[0],
                                hasEnglish: vocab.example_sentences[0] && typeof vocab.example_sentences[0] === 'object' && vocab.example_sentences[0] !== null && 'english' in vocab.example_sentences[0],
                                hasChinese: vocab.example_sentences[0] && typeof vocab.example_sentences[0] === 'object' && vocab.example_sentences[0] !== null && 'chinese' in vocab.example_sentences[0]
                              } : 'n/a'
                          })}
                        </div>
                        
                        {/* 调用独立的例句渲染函数，彻底解决IIFE解析问题 */}
                        {renderExampleSentences(vocab.example_sentences)}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-data" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
              {filterKeyword ? '没有找到匹配的词汇' : '该分类下无词汇'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VocabularyExtension;